import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { CONTENT_CAP } from '~/ui-rocco/pages/detail-shared';

interface EditorialCategoryHeroProps {
    /** The category name, rendered as the masthead. */
    title: string;
    /** Full-bleed backdrop; falls back to the flat ground when absent. */
    imageSrc?: string;
    className?: string;
}

/**
 * The category masthead: a full-bleed image with the category name centred over
 * it, under a `Featured` eyebrow.
 *
 * Geometry is Figma 49:4537 at 1920 — a 1920x843 frame with the title block
 * sitting 109.88px off the bottom. `1rem` is 16px at 1920, so the Figma pixel
 * values carry over as rem and scale with the viewport.
 */
export const EditorialCategoryHero = ({
    title,
    imageSrc,
    className,
}: EditorialCategoryHeroProps) => {
    return (
        <section
            aria-labelledby="editorial-category-title"
            className={cn(
                // Full-bleed past the layout's 1920 container, matching the
                // detail-page heroes; the masthead inside stays capped.
                'relative flex items-end justify-center overflow-hidden bg-[#131313]',
                'ml-[calc(50%-50vw)] w-screen',
                'aspect-[1920/843] max-md:aspect-[414/420]',
                className,
            )}
        >
            {imageSrc && (
                <Image
                    src={imageSrc}
                    alt=""
                    fill={true}
                    fit="cover"
                    loading="eager"
                    className="absolute inset-0"
                />
            )}

            {/*
             * Bottom-up scrim carrying the masthead. Figma runs #131313 at 89%
             * from 6% to transparent at 70% of a 590px band.
             */}
            <div
                aria-hidden={true}
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[36.875rem] bg-gradient-to-t from-[#131313]/[0.89] from-[6%] to-transparent to-[70%]"
            />

            <div
                className={cn(
                    CONTENT_CAP,
                    'relative flex flex-col items-center gap-[0.605rem] pb-[6.8675rem] max-md:pb-[2rem]',
                )}
            >
                <p className="text-center font-sans text-[1rem] leading-[1.4375rem] font-normal tracking-[0.34rem] text-white uppercase max-md:text-[0.75rem] max-md:tracking-[0.25rem]">
                    Featured
                </p>
                {/* m-0: the UA's default h1 margin is 0.67em, which at 120px
                    adds ~80px above and below the masthead. */}
                <h1
                    id="editorial-category-title"
                    className="m-0 text-center font-gestura text-[7.5rem] leading-[8.125rem] font-normal tracking-[-0.3rem] text-[#e5e5e5] max-md:text-[2.75rem] max-md:leading-[3rem] max-md:tracking-[-0.1rem]"
                >
                    {title}
                </h1>
            </div>
        </section>
    );
};
